import { useEffect, useState } from 'react';
import { History, AlertCircle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Past billing periods and what was used in each.
 *
 * The snapshots behind this have been written on every allowance rollover
 * since they were added, but nothing read them back — the history existed and
 * was simply invisible. Periods run anniversary to anniversary, so the range is
 * shown rather than a month name: "8 Sep – 8 Oct" is the thing the customer
 * actually paid for.
 *
 * Props:
 *   fetcher  () => Promise<{ data }>   — scoped by the caller (own company or super admin)
 */
const fmt = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const shortFmt = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
    : '—';

export default function UsageHistoryTable({ fetcher, title = 'Usage History' }) {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        fetcher()
            .then(r => { if (!cancelled) setRows(r.data || []); })
            .catch(e => { if (!cancelled) setError(e.message || 'Could not load usage history.'); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (error) {
        return (
            <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {error}
                </div>
            </div>
        );
    }

    if (rows === null) {
        return (
            <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
            </div>
        );
    }

    if (!rows.length) {
        return (
            <div className="rounded-xl border border-dashed border-border bg-muted/20 p-6 text-center">
                <History className="mx-auto h-6 w-6 text-muted-foreground/40" />
                <p className="mt-2 text-sm font-medium text-foreground">No completed periods yet</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                    A period is recorded here once the monthly allowance rolls over.
                </p>
            </div>
        );
    }

    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="flex items-center gap-2 border-b border-border bg-muted/20 px-4 py-2.5">
                <History className="h-3.5 w-3.5 text-muted-foreground" />
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
                <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">
                    {rows.length} period{rows.length === 1 ? '' : 's'}
                </span>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                            <th className="px-4 py-2 text-left font-semibold">Period</th>
                            <th className="px-3 py-2 text-left font-semibold">Plan</th>
                            <th className="px-3 py-2 text-right font-semibold">Interviews</th>
                            <th className="px-3 py-2 text-right font-semibold">Completed</th>
                            <th className="px-3 py-2 text-right font-semibold">Job Descriptions</th>
                            <th className="px-4 py-2 text-right font-semibold">Resumes</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {rows.map((r) => {
                            const limit = r.limitsAtTime?.maxInterviewsPerMonth;
                            const used = r.interviews?.scheduled ?? 0;
                            const overLimit = limit > 0 && used > limit;
                            return (
                                <tr key={`${r.periodMonth}-${r.periodStart}`} className="hover:bg-muted/20">
                                    <td className="whitespace-nowrap px-4 py-2 font-medium text-foreground">
                                        {shortFmt(r.periodStart)} – {fmt(r.periodEnd)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                                        {r.planName || '—'}
                                    </td>
                                    <td className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${overLimit ? 'font-semibold text-amber-600' : 'text-foreground'}`}>
                                        {used}{limit > 0 ? <span className="text-muted-foreground"> / {limit}</span> : null}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                                        {r.interviews?.completed ?? 0}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                                        {r.jobDescriptions?.created ?? 0}
                                    </td>
                                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                                        {r.resumes?.received ?? 0}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
